import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { PortalBranding, PortalClientSummary } from "../types";

/**
 * Portal session store — the taxpayer-facing counterpart to `useAuthStore`,
 * kept entirely separate so a client session and a staff session never mix.
 * Holds the short-lived portal session token plus the bound client summary and
 * firm branding. The onboarding-wizard token is transient (in-page state), not
 * persisted here.
 */
interface PortalSessionState {
  sessionToken: string | null;
  client: PortalClientSummary | null;
  branding: PortalBranding | null;
  /**
   * The firm handle from the resolved portal URL (the `:portalName` route param
   * the taxpayer actually signed in through) — distinct from the firm-config
   * `branding.portalName`, which defaults blank. Kept across `clearSession()` so
   * sign-out/401 always knows which firm's branded sign-in to return to.
   */
  portalName: string | null;
  isPortalAuthenticated: boolean;
  setSession: (token: string, client: PortalClientSummary, branding: PortalBranding) => void;
  setClient: (client: PortalClientSummary) => void;
  /** Brand the shell on public pages (resolve/onboarding) without authenticating. */
  setBranding: (branding: PortalBranding) => void;
  /** Remember the resolved firm handle (see `portalName`). */
  setPortalName: (name: string) => void;
  clearSession: () => void;
}

export const usePortalSessionStore = create<PortalSessionState>()(
  persist(
    (set) => ({
      sessionToken: null,
      client: null,
      branding: null,
      portalName: null,
      isPortalAuthenticated: false,
      setSession: (sessionToken, client, branding) =>
        set({ sessionToken, client, branding, isPortalAuthenticated: true }),
      setClient: (client) => set({ client }),
      setBranding: (branding) => set({ branding }),
      setPortalName: (portalName) => set({ portalName }),
      // Keep `portalName` so the branded sign-in stays reachable after sign-out.
      clearSession: () =>
        set({ sessionToken: null, client: null, branding: null, isPortalAuthenticated: false }),
    }),
    {
      name: "portal-session-storage",
      partialize: (state) => ({
        sessionToken: state.sessionToken,
        client: state.client,
        branding: state.branding,
        portalName: state.portalName,
        isPortalAuthenticated: state.isPortalAuthenticated,
      }),
    },
  ),
);
